/**
 * デバイス別FWバージョン一覧ページ
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { firmwareApi } from '../services/api';
import { Icon } from '../components/icons';
import { Button, Card, Alert, Select } from '../components/ui';

const OTA_STATUS_LABELS = {
  success: { label: '成功', color: 'bg-green-100 text-green-700' },
  failed: { label: '失敗', color: 'bg-red-100 text-red-700' },
};

export default function DeviceFirmware() {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [versionSummary, setVersionSummary] = useState({});
  const [firmware, setFirmware] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // フィルター
  const [filterVersion, setFilterVersion] = useState('');
  const [filterOtaStatus, setFilterOtaStatus] = useState('');

  // データ取得
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterVersion) params.version = filterVersion;
      if (filterOtaStatus) params.otaStatus = filterOtaStatus;

      const [deviceData, fwData] = await Promise.all([
        firmwareApi.listDevices(params),
        firmwareApi.list(),
      ]);

      setDevices(deviceData.devices || []);
      setVersionSummary(deviceData.versionSummary || {});
      setFirmware(fwData.firmware || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filterVersion, filterOtaStatus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (isoStr) => {
    if (!isoStr) return '-';
    return new Date(isoStr).toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 最新リリースバージョンを取得
  const latestReleasedVersion = firmware
    .filter((fw) => fw.status === 'released')
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]?.version;

  // バージョン別の色分け
  const getVersionColor = (version) => {
    if (!version || version === 'unknown') return 'bg-slate-100 text-slate-600';
    if (version === latestReleasedVersion) return 'bg-green-100 text-green-700';
    return 'bg-yellow-100 text-yellow-700';
  };

  // バージョン一覧（サマリー用）
  const versionList = Object.entries(versionSummary)
    .sort(([a], [b]) => {
      if (a === 'unknown') return 1;
      if (b === 'unknown') return -1;
      return b.localeCompare(a, undefined, { numeric: true });
    });

  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-500">デバイス別のファームウェアバージョン管理</p>

      {error && <Alert variant="error" onClose={() => setError(null)}>{error}</Alert>}

      {/* サマリーカード */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
              <Icon name="device" className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{devices.length}</p>
              <p className="text-xs text-slate-500">総デバイス数</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Icon name="check" className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {versionSummary[latestReleasedVersion] || 0}
              </p>
              <p className="text-xs text-slate-500">最新バージョン</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Icon name="alertCircle" className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {devices.filter((d) => d.firmwareVersion !== latestReleasedVersion && d.firmwareVersion !== 'unknown').length}
              </p>
              <p className="text-xs text-slate-500">要更新</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
              <Icon name="info" className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {versionSummary['unknown'] || 0}
              </p>
              <p className="text-xs text-slate-500">不明</p>
            </div>
          </div>
        </Card>
      </div>

      {/* バージョン分布 */}
      <Card title="バージョン分布">
        <div className="flex flex-wrap gap-3">
          {versionList.map(([version, count]) => (
            <button
              key={version}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterVersion === version
                  ? 'ring-2 ring-primary-500'
                  : ''
              } ${getVersionColor(version)}`}
              onClick={() => setFilterVersion(filterVersion === version ? '' : version)}
            >
              {version === 'unknown' ? '不明' : `v${version}`}
              <span className="ml-2 font-bold">{count}</span>
            </button>
          ))}
        </div>
        {latestReleasedVersion && (
          <p className="mt-3 text-xs text-slate-500">
            最新リリース: v{latestReleasedVersion}
          </p>
        )}
      </Card>

      {/* フィルター & テーブル */}
      <Card padding="p-0">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-4">
          <Select
            value={filterVersion}
            onChange={(e) => setFilterVersion(e.target.value)}
            options={[
              { value: '', label: 'すべてのバージョン' },
              ...versionList.map(([v]) => ({
                value: v,
                label: v === 'unknown' ? '不明' : `v${v}`,
              })),
            ]}
          />
          <Select
            value={filterOtaStatus}
            onChange={(e) => setFilterOtaStatus(e.target.value)}
            options={[
              { value: '', label: 'すべてのOTAステータス' },
              { value: 'success', label: '成功' },
              { value: 'failed', label: '失敗' },
            ]}
          />
          <Button variant="secondary" onClick={fetchData}>
            <Icon name="refresh" className="w-4 h-4" />
            更新
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : devices.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            デバイスなし
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">デバイス</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">FWバージョン</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">ステータス</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">最終OTA</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">OTA結果</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">最終接続</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {devices.map((device) => (
                  <tr
                    key={device.thingName}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/devices/${device.thingName}`)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Icon name="drone" className="w-4 h-4 text-slate-400" />
                        <span className="text-sm font-medium text-slate-900">{device.thingName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${getVersionColor(device.firmwareVersion)}`}>
                        {device.firmwareVersion === 'unknown' ? '不明' : `v${device.firmwareVersion}`}
                      </span>
                      {device.previousFirmwareVersion && (
                        <span className="ml-2 text-xs text-slate-400">
                          (前: v{device.previousFirmwareVersion})
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {device.lifecycleStatus}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {formatDate(device.lastOtaAt)}
                    </td>
                    <td className="px-4 py-3">
                      {device.lastOtaStatus ? (
                        <div>
                          <span className={`px-2 py-1 text-xs rounded-full ${OTA_STATUS_LABELS[device.lastOtaStatus]?.color || 'bg-slate-100'}`}>
                            {OTA_STATUS_LABELS[device.lastOtaStatus]?.label || device.lastOtaStatus}
                          </span>
                          {device.lastOtaError && (
                            <p className="mt-1 text-xs text-red-500 truncate max-w-xs" title={device.lastOtaError}>
                              {device.lastOtaError}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {formatDate(device.lastConnectedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
